import React, {useEffect, useState} from "react";
import {Table, Card, Typography, Button, Modal, Form, InputNumber, message, Select, Col, Row} from "antd";
import api from "../../api";
import dayjs from "dayjs";

const {Title} = Typography;
const {Option} = Select;

const DolphinRatesPage: React.FC = () => {
    const [rates, setRates] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [pagination_cc, setPagination_cc] = useState({
        current: 1,
        pageSize: 10,
    });
    const [pagination_walkin, setPagination_walkin] = useState({
        current: 1,
        pageSize: 10,
    });
    const [form] = Form.useForm();

    useEffect(() => {
        fetchRates();
    }, []);

    const fetchRates = async () => {
        setLoading(true)
        try {
            const res = await api.get("/dolphin/rates/");
            setRates(res.data);
        } catch (err) {
            message.error("Failed to load rates");
        } finally {
            setLoading(false);
        }
    };

    const handleRateUpdate = async (values: any) => {
        try {
            await api.post("/dolphin/rates/", values);
            message.success("Rates updated successfully");
            setModalOpen(false);
            fetchRates();
        } catch (err) {
            message.error("Failed to update rates");
        }
    };

    const ccRates = rates.filter((rate) => rate.rate_type === "cc");
    const walkinRates = rates.filter((rate) => rate.rate_type === "walkin");

    const rateColumns = [
        {
            title: "Timestamp",
            dataIndex: "timestamp",
            key: "timestamp",
            render: (val: string) => dayjs(val).format("YYYY-MM-DD HH:mm:ss")
        },
        {title: "Yen → KRW", dataIndex: "yen_buy", key: "yen_buy"},
        {title: "KRW → Yen", dataIndex: "yen_sell", key: "yen_sell"},
        {title: "USD → KRW", dataIndex: "usd_buy", key: "usd_buy"},
        {title: "KRW → USD", dataIndex: "usd_sell", key: "usd_sell"},
        {title: "HKD → KRW", dataIndex: "hkd_buy", key: "hkd_buy"},
        {title: "KRW → HKD", dataIndex: "hkd_sell", key: "hkd_sell"},
        {title: "CNY → KRW", dataIndex: "cny_buy", key: "cny_buy"},
        {title: "KRW → CNY", dataIndex: "cny_sell", key: "cny_sell"},
        {title: "EUR → KRW", dataIndex: "euro_buy", key: "euro_buy"},
        {title: "KRW → EUR", dataIndex: "euro_sell", key: "euro_sell"},
        {title: "USDT-TRC20 → KRW", dataIndex: "usdt_trc_buy", key: "usdt_trc_buy"},
        {title: "KRW → USDT-TRC20", dataIndex: "usdt_trc_sell", key: "usdt_trc_sell"},
        {title: "USDT-ERC20 → KRW", dataIndex: "usdt_erc_buy", key: "usdt_erc_buy"},
        {title: "KRW → USDT-ERC20", dataIndex: "usdt_erc_sell", key: "usdt_erc_sell"},
        // {title: "KRW → USDT", dataIndex: "usdt_sell", key: "usdt_sell"},
    ];

    return (
        <Card
            title={
                <Row justify="space-between" align="middle" gutter={16}>
                    <Col>
                        <Title level={3} style={{margin: 0}}>
                            Dolphin Exchange Rates
                        </Title>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={() => setModalOpen(true)}>
                            Update Rates
                        </Button>
                    </Col>
                </Row>
            }
            style={{margin: 16}}
        >
            <Title level={4}>Casino Club Rates</Title>
            <Table
                loading={loading}
                rowKey={(record) => record.id}
                dataSource={ccRates}
                columns={rateColumns}
                pagination={{
                    ...pagination_cc,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination_cc({current: page, pageSize});
                    },
                }}
                scroll={{x: "max-content"}}
                onRow={(_, index) => ({
                    style: {
                        backgroundColor: index === 0 ? "#ffffff" : "#f0f0f0",
                        color: index === 0 ? "inherit" : "#888",
                    },
                })}
            />

            <Title level={4} style={{marginTop: 32}}>Walk-in Rates</Title>
            <Table
                loading={loading}
                rowKey={(record) => record.id + "-walkin"}
                dataSource={walkinRates}
                columns={rateColumns}
                pagination={{
                    ...pagination_walkin,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination_walkin({current: page, pageSize});
                    },
                }}
                scroll={{x: "max-content"}}
                onRow={(_, index) => ({
                    style: {
                        backgroundColor: index === 0 ? "#ffffff" : "#f0f0f0",
                        color: index === 0 ? "inherit" : "#888",
                    },
                })}
            />

            <Modal
                title="Update Exchange Rates"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Save"
                width={600}
                bodyStyle={{maxHeight: "70vh", overflowY: "auto"}}
            >
                <Form form={form} layout="vertical" onFinish={handleRateUpdate}>
                    <Form.Item name="rate_type" label="Rate Type" rules={[{required: true}]}>
                        <Select placeholder="Select Rate Type">
                            <Option value="cc">Casino Club Rates</Option>
                            <Option value="walkin">Walk-in Rates</Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Yen → KRW" name="yen_buy" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="KRW → Yen" name="yen_sell" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label="USD → KRW" name="usd_buy">
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="KRW → USD" name="usd_sell">
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="HKD → KRW" name="hkd_buy" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="KRW → HKD" name="hkd_sell" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="CNY → KRW" name="cny_buy" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="KRW → CNY" name="cny_sell" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="EUR → KRW" name="euro_buy">
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="KRW → EUR" name="euro_sell">
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label="USDT-TRC20 → KRW" name="usdt_trc_buy" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="KRW → USDT-TRC20" name="usdt_trc_sell" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="USDT-ERC20 → KRW" name="usdt_erc_buy" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="KRW → USDT-ERC20" name="usdt_erc_sell" rules={[{required: true}]}>
                                <InputNumber style={{width: "100%"}} step={0.01}/>
                            </Form.Item>
                        </Col>
                        {/*<Col span={24}>*/}
                        {/*    <Form.Item label="KRW → USDT" name="usdt_sell" rules={[{required: true}]}>*/}
                        {/*        <InputNumber style={{width: "100%"}} step={0.01}/>*/}
                        {/*    </Form.Item>*/}
                        {/*</Col>*/}
                    </Row>
                </Form>
            </Modal>
        </Card>

    );
};

export default DolphinRatesPage;
